import {
  Activity,
  BookingEvents,
  MenuItemKeys,
  Planning,
  Reservation,
  Settings,
  SettingsEnum,
  Stores,
  SupervisionedWorkoutsTimetable,
  User,
  VideoCategory,
  VimeoVideo,
  VimeoVideoCategory,
} from "../types";
import { makeAutoObservable } from "mobx";
import api from "../services/api";
import dayjs from "dayjs";
import config from "../config/config";

export const getEventTitle = (activity: Activity) => {
  let title = "";

  switch (activity.type) {
    case "online-traning":
      title = activity.eventMeta.video?.name;
      break;
    case "long-live-traning":
      title = "Allenamento con supervisione";
      break;
    default:
  }

  return title;
};

export const getEventPlayedSeconds = (activity: Activity): number => {
  let duration = 0;

  switch (activity.type) {
    case "online-traning":
      duration = activity.eventMeta.log?.playedSeconds || 0;
      break;
    default:
  }

  return duration;
};

export const getReservationTitle = (reservation: Reservation) => {
  let title = "-";

  switch (reservation.type) {
    case "calendly.event_scheduled":
      title = reservation.eventMeta?.name;
      break;
    default:
  }

  return title;
};

export class SessionStore {
  stores: Stores;

  isInitialized = false;
  user: User | null = null;
  isSubmitting = false;
  sessionToken: string = "";

  // TODO: Move to another store
  isLoadingVimeoVideoCategories = false;
  vimeoVideoCategories: VimeoVideoCategory[] = [];
  isLoadingVideoCategories = false;
  videoCategories: VideoCategory[] = [];

  isFetchingVimeoVideo = false;
  videoList: VimeoVideo[] = [];

  settings: Settings[] = [];

  // calendlySessionCoachingUrl: Settings | null = null;
  // calendlyLiveTrainingUrl: Settings | null = null;

  stripeCheckoutError = false;
  stripeSessionCheckoutError = false;
  stripePortalError = false;

  activities: Activity[] = [];
  reservations: Reservation[] = [];
  calendarReservations: Reservation[] = [];
  calendarPeriod: [Date, Date] = [new Date(), new Date()];
  plannings: Planning[] = [];

  isLoadingZoom = false;
  zoomSignature: string = "";

  get isLogged() {
    return this.user !== null;
  }

  get isTrialMode() {
    return (
      this.user?.role === "user" && this.user.subscription_type === "TRIAL"
    );
  }

  get isPTSubscription() {
    return this.user && this.user.subscription_type === "PT";
  }

  get isSubscriptionDeactivated() {
    return (
      this.user?.subscription_disactivation_date &&
      dayjs(this.user?.subscription_disactivation_date).isBefore(dayjs())
    );
  }

  get isTrialExpired() {
    if (
      !this.user ||
      (this.user && this.user.subscriptionID) ||
      (this.user && this.user.subscription_type === "PT")
    ) {
      return false;
    }

    const endTrialDate = new Date(this.user.createdAt);
    endTrialDate.setDate(endTrialDate.getDate() + config.TRIAL_DAYS);

    const diffDays = dayjs(endTrialDate).diff(dayjs(), "days");
    return diffDays <= 0;
  }

  get calendlySessionCoachingUrl() {
    return this.settings.find(
      (setting) => setting.key === SettingsEnum.CALENDLY_COACHING_SESSION_URL
    );
  }

  get calendlyLiveTrainingUrl() {
    return this.settings.find(
      (setting) => setting.key === SettingsEnum.CALENDLY_LIVE_TRAINING_URL
    );
  }

  get supervisionedWorkoutStep1Video() {
    return this.settings.find(
      (setting) =>
        setting.key === SettingsEnum.SUPERVISED_WORKOUTS_STEP_1_VIDEO_URL
    );
  }

  get supervisionedWorkoutStep1Message() {
    return this.settings.find(
      (setting) =>
        setting.key === SettingsEnum.SUPERVISED_WORKOUTS_STEP_1_MESSAGE
    );
  }

  get supervisionedWorkoutStep2ZoomLink() {
    return this.settings.find(
      (setting) =>
        setting.key === SettingsEnum.SUPERVISED_WORKOUTS_STEP_2_ZOOM_LINK
    );
  }

  get supervisionedWorkoutStep2Message() {
    return this.settings.find(
      (setting) =>
        setting.key === SettingsEnum.SUPERVISED_WORKOUTS_STEP_2_MESSAGE
    );
  }

  get supervisionedWorkoutStep3Video() {
    return this.settings.find(
      (setting) =>
        setting.key === SettingsEnum.SUPERVISED_WORKOUTS_STEP_3_VIDEO_URL
    );
  }

  get supervisionedWorkoutStep3Message() {
    return this.settings.find(
      (setting) =>
        setting.key === SettingsEnum.SUPERVISED_WORKOUTS_STEP_3_MESSAGE
    );
  }

  get supervisionedWorkoutTimetable() {
    const value = this.settings.find(
      (setting) => setting.key === SettingsEnum.SUPERVISED_WORKOUTS_TIMETABLES
    );
    return value
      ? (JSON.parse(value.value) as SupervisionedWorkoutsTimetable)
      : undefined;
  }

  get worksoutTimetable() {
    return this.settings.find(
      (setting) => setting.key === SettingsEnum.WORKOUTS_TIMETABLES
    );
  }

  get calendlyBookPersonalTrainerActiveSubscription() {
    return this.settings.find(
      (setting) =>
        setting.key ===
        SettingsEnum.CALENDLY_BOOK_PERSONAL_TRAINER_ACTIVE_SUBSCRIPTION
    );
  }

  get calendlyBookPersonalTrainerInctiveSubscription() {
    return this.settings.find(
      (setting) =>
        setting.key ===
        SettingsEnum.CALENDLY_BOOK_PERSONAL_TRAINER_INACTIVE_SUBSCRIPTION
    );
  }

  get calendarEvents(): any[] {
    // return [
    //   {
    //     events: this.activities.map((activity) => ({
    //       id: activity.id,
    //       title: getEventTitle(activity),
    //       start: activity.dateTime,
    //       end: activity.dateTime,
    //       //color: event.color,
    //       extendedProps: {
    //         ...activity.eventMeta,
    //         type: "activity",
    //       },
    //     })),
    //   },
    //   {
    //     events: this.calendarReservations.map((reservation) => ({
    //       id: reservation.id,
    //       title: getReservationTitle(reservation),
    //       start: reservation.dateTime,
    //       end: reservation.dateTime,
    //       extendedProps: {
    //         ...reservation.eventMeta,
    //         type: "reservation",
    //       },
    //     })),
    //   },
    // ];

    const activities = this.activities.map((activity) => ({
      id: activity.id,
      title: getEventTitle(activity),
      start: activity.dateTime,
      end: activity.dateTime,
      //color: event.color,
      extendedProps: {
        ...activity.eventMeta,
        type: "activity",
        category: activity.type,
      },
    }));

    const events = this.calendarReservations.map((reservation) => ({
      id: reservation.id,
      title: getReservationTitle(reservation),
      start: reservation.dateTime,
      end: reservation.dateTime,
      extendedProps: {
        ...reservation.eventMeta,
        type: "reservation",
      },
    }));

    return activities.concat(events);
  }

  constructor(stores: Stores) {
    this.stores = stores;
    makeAutoObservable(this);
  }

  initialize = async () => {
    try {
      const user = await api.getCurrentUser();
      if (user) {
        this.restoreSession();
        this.user = user;
      } else {
        this.user = null;
      }
    } catch (err) {
      console.log(err);
    } finally {
      this.isInitialized = true;
    }
  };

  signUp = async (
    name: User["name"],
    surname: User["surname"],
    email: User["email"],
    password: string,
    phone: User["phone"]
  ) => {
    this.isSubmitting = true;
    try {
      const user = await api.signUp(name, surname, email, password, phone);
      this.user = { ...user };
      this.restoreSession();
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  login = async (email: User["email"], password: string) => {
    this.isSubmitting = true;
    try {
      await api.login(email, password);
      this.initialize();
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  sendRecoveryLink = async (email: User["email"]) => {
    this.isSubmitting = true;

    try {
      await api.lostPassword(email);
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  changePassword = async (
    email: User["email"],
    password: string,
    token: string
  ) => {
    this.isSubmitting = true;

    try {
      await api.changePassword(email, password, token);
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  changeSessionUserPassword = async (newPassword: string) => {
    if (!this.user) {
      return false;
    }

    this.isSubmitting = true;

    try {
      await api.changeSessionUserPassword(this.user.email, newPassword);
      if (!this.user.signupCompleted) {
        this.user.signupCompleted = true;
      }
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  restoreSession = async () => {
    const data = localStorage.getItem("session");
    const sessionData = data ? JSON.parse(data) : null;

    if (sessionData) {
      this.sessionToken = sessionData.token;
    }
  };

  persistSession = async () => {
    const data = {
      token: this.sessionToken,
    };
    localStorage.setItem("session", JSON.stringify(data));
  };

  resetSession = () => {
    this.user = null;
    this.sessionToken = "";
    localStorage.removeItem("session");
  };

  // updateUser = async (data: any) => {
  //   if (this.user) {
  //     try {
  //       const user = await api.updateUser(this.user?.uid, {
  //         ...this.user,
  //         ...data,
  //       });

  //       this.user = { ...this.user, termsOfUse: user.termsOfUse };
  //     } catch (err) {
  //       console.log(err);
  //     }
  //   }
  // };

  //TODO: add to all the stripe functions a check of the session. If not avaible return and redirect to login

  stripeCheckout = async () => {
    this.stripeCheckoutError = false;
    this.isSubmitting = true;

    try {
      if (this.user) {
        await api.stripeCheckout(this.user.email);
      }

      //TODO: if success set user state to "PAID SUBSCRIPTION"
    } catch (err) {
      console.log(err);
      this.stripeCheckoutError = true;
    } finally {
      this.isSubmitting = false;
    }
  };

  stripeSessionCheckout = async (sessionId: string) => {
    this.stripeSessionCheckoutError = false;
    this.isSubmitting = true;

    try {
      const stripeSession = await api.stripeSessionCheckout(sessionId);

      // this.user!.stripeID = stripeSession.customer;
      // this.user!.subscriptionID = stripeSession.subscription;
      this.initialize();
      //TODO: if success save user stripe ID into the backend to be able to cancel subscription later
    } catch (err) {
      console.log(err);
      this.stripeSessionCheckoutError = true;
    } finally {
      this.isSubmitting = false;
    }
  };

  stripeAccessPortal = async (): Promise<string> => {
    this.stripePortalError = false;
    this.isSubmitting = true;

    try {
      // TODO: add customer ID from user data
      if (this.user) {
        const portalSessionUrl = await api.stripeAccessPortal(
          this.user.stripeID || ""
          //"cus_Jyj9iVSE1fqHVv"
        );
        return portalSessionUrl;
      }
      return "";
    } catch (err) {
      console.log(err);
      this.stripePortalError = true;
      return "";
    } finally {
      this.isSubmitting = false;
    }
  };

  // TODO: Move to another store
  getVimeoVideoCategories = async () => {
    this.isLoadingVimeoVideoCategories = true;

    try {
      this.vimeoVideoCategories = await api.getVimeoVideoCategories();
    } finally {
      this.isLoadingVimeoVideoCategories = false;
    }
  };

  getVideoCategories = async () => {
    this.isLoadingVideoCategories = true;

    try {
      this.videoCategories = await api.getVideoCategories();
    } finally {
      this.isLoadingVideoCategories = false;
    }
  };

  getVideoList = async (externalCategoryId: VideoCategory["externalId"]) => {
    this.isFetchingVimeoVideo = true;
    this.videoList = [];
    try {
      this.videoList = await api.getVideoList(externalCategoryId);
    } catch (err) {
      console.log(err);
    } finally {
      this.isFetchingVimeoVideo = false;
    }
  };

  addVideoCategories = async (videoCategory: VideoCategory) => {
    delete videoCategory.id;
    const response = await api.addVideoCategories(videoCategory);

    this.videoCategories = [...this.videoCategories, response];
  };

  updateVideoCategories = async (videoCategory: VideoCategory) => {
    const response = await api.updateVideoCategories(videoCategory);

    this.videoCategories = this.videoCategories.map((videoCategory) =>
      videoCategory.id === response.id ? response : videoCategory
    );
  };

  getSettings = async () => {
    try {
      this.settings = await api.getSettings();
    } catch (err) {
      console.log(err);
    }
  };

  updateSettings = async (key: Settings["key"], value: Settings["value"]) => {
    await api.updateSettings({ key, value });
    this.settings = this.settings.map((setting) =>
      setting.key === key ? { key, value } : setting
    );
  };

  traceActivity = async (activity: Partial<Activity>) => {
    try {
      await api.traceActivity({
        ...activity,
        userId: this.user?.id,
      });
    } catch (err) {
      console.log(err);
    }
  };

  fetchActivities = async (fromDate: Date, toDate: Date, userId?: string) => {
    try {
      if (this.user) {
        const activities = await api.getActivities(
          userId ? userId : this.user.id,
          fromDate,
          toDate
        );

        this.activities = activities;
      }
    } catch (err) {
      console.log(err);
    }
  };

  traceReservation = async (
    reservation: Partial<Reservation> & { bookingEvent?: BookingEvents }
  ) => {
    try {
      // const newReservation = await api.traceReservation({
      //   ...reservation,
      //   userId: this.user?.id,
      // });
      setTimeout(() => {
        this.fetchReservations();
        if (this.user) {
          this.fetchCalendarReservations(
            this.calendarPeriod[0],
            this.calendarPeriod[1],
            this.user.id
          );
        }
        this.initialize();
      }, 2000);

      // this.calendarReservations.push(newReservation);

      // if (
      //   reservation.bookingEvent ===
      //     "BOOK_PERSONAL_TRAINER_INACTIVE_SUBSCRIPTION" ||
      //   this.user?.subscription_type === "PT"
      // ) {
      //   this.initialize();
      // }
    } catch (err) {
      console.log(err);
    }
  };

  fetchReservations = async () => {
    try {
      if (this.user) {
        const reservations = await api.getReservations(
          this.user.id,
          new Date(new Date().setHours(0, 0, 0, 0)),
          new Date("2099-12-31")
        );

        this.reservations = reservations;
      }
    } catch (err) {
      console.log(err);
    }
  };

  removeReservation = async (reservationId: number) => {
    try {
      const response = await api.removeReservation(reservationId);

      this.calendarReservations = this.calendarReservations.filter(
        (reservation) => reservation.id !== response.id
      );

      this.reservations = this.reservations.filter(
        (reservation) => reservation.id !== response.id
      );
    } catch (err) {
      console.log(err);
    }
  };

  fetchCalendarReservations = async (
    fromDate: Date,
    toDate: Date,
    userId?: string
  ) => {
    try {
      if (this.user) {
        const reservations = await api.getReservations(
          userId ? userId : this.user.id,
          fromDate,
          toDate
        );

        this.calendarPeriod = [fromDate, toDate];
        this.calendarReservations = reservations;
      }
    } catch (err) {
      console.log(err);
    }
  };

  fetchPlanning = async (fromDate: Date, toDate: Date, userId?: string) => {
    try {
      if (this.user) {
        const plannings = await api.getPlannings(
          userId ? userId : this.user.id,
          fromDate,
          toDate
        );

        this.plannings = plannings;
      }
    } catch (err) {
      console.log(err);
    }
  };

  addPlanning = async (data: Partial<Planning>, userId?: string) => {
    try {
      if (this.user) {
        const planning = await api.addPlanning(this.user?.id, {
          ...data,
          userId: userId || this.user.id,
        });

        this.plannings.push(planning);
      }
    } catch (err) {}
  };

  updatePlanning = async (data: Partial<Planning>, userId?: string) => {
    try {
      if (this.user && data.id) {
        const planning = await api.updatePlanning(this.user.id, data.id, {
          ...data,
          userId: userId || this.user.id,
        });

        this.plannings = this.plannings.map((p) =>
          p.id === planning.id ? planning : p
        );
      }
    } catch (err) {}
  };

  deletePlanning = async (planningId: Planning["id"], userId?: string) => {
    try {
      if (this.user) {
        const planning = await api.removePlanning(this.user.id, planningId);

        this.plannings = this.plannings.filter((p) => p.id !== planning.id);
      }
    } catch (err) {}
  };
}
